import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Wand2, ArrowRight } from 'lucide-react';

interface PromptOptimizeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originalPrompt: string;
  optimizedPrompt: string;
  onApply: () => void;
  isLoading?: boolean;
  promptType: 'system' | 'user';
}

export function PromptOptimizeDialog({
  open,
  onOpenChange,
  originalPrompt,
  optimizedPrompt,
  onApply,
  isLoading = false,
  promptType,
}: PromptOptimizeDialogProps) {
  const handleApply = () => {
    onApply();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-muted-foreground" />
            Optimise {promptType === 'system' ? 'System' : 'User'} Prompt
          </DialogTitle>
          <DialogDescription>
            Review the AI-optimised version of your prompt. Click Apply to use the optimised version.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Optimising your prompt...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 h-full">
              {/* Original Prompt */}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  Original Prompt
                </Label>
                <Textarea
                  value={originalPrompt}
                  readOnly
                  className="flex-1 min-h-[300px] max-h-[400px] resize-none font-mono text-sm bg-muted/50"
                />
              </div>

              {/* Arrow indicator */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden sm:flex">
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
              </div>

              {/* Optimized Prompt */}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">
                  Optimised Prompt
                </Label>
                <Textarea
                  value={optimizedPrompt}
                  readOnly
                  className="flex-1 min-h-[300px] max-h-[400px] resize-none font-mono text-sm border-primary/20 bg-primary/5"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={isLoading || !optimizedPrompt}
          >
            <Wand2 className="h-4 w-4 mr-2" />
            Apply Optimised Prompt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
